import React, { Fragment } from 'react';
import styled from 'styled-components';
import { ViewEditAdd } from '../../hocs';
import { InlinePageTitle, Panel } from '../../molecules';
import { Button, Date, Field, Label, Link, TextEditorInput, TextInput } from '../../atoms';
import { APPLICATION_LINK_TYPES } from '../../constants';
import { useAuthorization } from '../../auth';
import config from '../../config';

const Job = ({ ...props }) => {
    const { isClient, client } = useAuthorization();
    const { data } = props;

    return (
        <Fragment>
            <InlinePageTitle title="Job">
                <HeaderButtons>
                    <Button to={`https://progress-hub.work180.com/e/${data.employer.slug}-${data.employer.id}/employer-profile/jobs?jobId=${data.id}`} isExternal={true}>
                        View job in Progress Hub
                    </Button>
                    {data.applicationType === 'WORK180' && (
                        <Button to={`/applications/all?jobId=${data.id}`} filled="border">
                            View applications
                        </Button>
                    )}
                </HeaderButtons>
            </InlinePageTitle>
            <Fragment>
                <Field
                    name="title"
                    initialValue={data.title}
                    displayValueAs={<h2>{data.title}</h2>}
                    fieldType="text"
                    initialRenderType={'value'}
                    isTogglingEnabled={false}
                    handleSubmit={() => {}}
                    disabled
                />
                <Panel title="Position Details">
                    <Field
                        label="Reference Number"
                        name="reference"
                        initialValue={data.reference}
                        initialRenderType={'value'}
                        isTogglingEnabled={false}
                        fieldType="text"
                        handleSubmit={() => {}}
                        disabled
                    />
                    <Field
                        label="Work Type"
                        name="workTypes"
                        initialValue={
                            <span>{!!data.workTypes && !!data.workTypes.length ? data.workTypes.map(workType => workType.name).join(', ') : 'Choose worktype'}</span>
                        }
                        initialRenderType={'value'}
                        isTogglingEnabled={false}
                        fieldType="text"
                        handleSubmit={() => {}}
                        disabled
                    />
                    <Field
                        label="Location"
                        name="locations"
                        initialValue={
                            <span>
                                {!!data.locations && !!data.locations.length ? data.locations.map(item => item.sourceText || item.label).join(', ') : 'Choose location'}
                            </span>
                        }
                        initialRenderType={'value'}
                        isTogglingEnabled={false}
                        fieldType="text"
                        handleSubmit={() => {}}
                        disabled
                    />
                    <Separator>
                        <span>Or</span>
                    </Separator>

                    <Field
                        label="Remote"
                        name="isRemote"
                        initialValue={data.isRemote}
                        fieldType="checkbox"
                        handleSubmit={() => {}}
                        isTogglingEnabled={false}
                        hideOptionalLabel={true}
                        disabled
                    />
                    <Field
                        label="Level Of Experience"
                        name="levelOfExperience"
                        initialValue={data.levelOfExperience ? data.levelOfExperience.id : null}
                        initialRenderType={'value'}
                        isTogglingEnabled={false}
                        fieldType="text"
                        handleSubmit={() => {}}
                        disabled
                    />
                </Panel>
                {isClient && <TextInput name="employerSlug" initialValue={client} isTogglingEnabled={false} disabled style={{ display: 'none' }} />}
                {!isClient && (
                    <Panel title="Employer Details">
                        {data.employer && (
                            <Fragment>
                                <Label label="Name" isInline={true}>
                                    <Link to={`/employers/${data.employer.id}`}>{data.employer && data.employer.name}</Link>
                                </Label>
                                <Link to={`/employers/${data.employer.id}`} hoverTheme="outline">
                                    <Logo>
                                        <img src={data.employer && data.employer.logo} alt="Logo" />
                                    </Logo>
                                </Link>
                                <Button
                                    to={{
                                        pathname: '/jobs/all',
                                        search: `?employer=${data.employer.slug}`
                                    }}
                                    icon="ArrowRight"
                                    filled="border"
                                    style={{ marginBottom: '1rem' }}
                                >
                                    View All Jobs
                                </Button>
                            </Fragment>
                        )}
                    </Panel>
                )}
                <Panel title="Job Board Details">
                    <Field
                        label="Application"
                        name="applicationType"
                        initialValue={data.applicationType}
                        includeBefore="Receive applications through:"
                        disabled
                        isTogglingEnabled={false}
                        displayValueAs={<span>{data.applicationType && APPLICATION_LINK_TYPES.filter(type => type.id === data.applicationType)[0].name}</span>}
                        fieldType="radio"
                        includeAfter={
                            data.applicationType === 'WORK180' && (
                                <InlineField>
                                    <Field
                                        label="Send notification of new applications to:"
                                        name="sendNotification"
                                        // Set sendNotification to checked if not set
                                        initialValue={!!(data.applicationType === 'WORK180' && data.applicationEmail)}
                                        fieldType="checkbox"
                                        isInline
                                        hideOptionalLabel
                                        disabled
                                        handleSubmit={() => {}}
                                    />
                                </InlineField>
                            )
                        }
                        options={APPLICATION_LINK_TYPES}
                        handleSubmit={() => {}}
                    />
                    {data.applicationType === 'external' && (
                        <Field
                            label="External Link"
                            name="applicationLink"
                            initialValue={data.applicationLink}
                            fieldType="link"
                            isTogglingEnabled={false}
                            handleSubmit={() => {}}
                            disabled
                        />
                    )}
                    {(data.applicationType === 'email' || (data.applicationType === 'WORK180' && data.sendNotification)) && (
                        <Field
                            label="Email"
                            name="applicationEmail"
                            extraDetail="Up to 5 emails can be added"
                            // Set user's emails as the default if not set
                            initialValue={data.applicationEmail ? data.applicationEmail : []}
                            displayValueAs={<span>{!!data.applicationEmail && !!data.applicationEmail.length ? data.applicationEmail.join(', ') : 'Choose emails'}</span>}
                            fieldType="tag"
                            isTogglingEnabled={false}
                            handleSubmit={() => {}}
                            disabled
                        />
                    )}
                    <Field
                        label="Url"
                        name="slug"
                        initialValue={data.slug}
                        includeBefore={
                            <Url>
                                {config.website}/for-women/job/{data.id}/
                            </Url>
                        }
                        fieldType="link"
                        handleSubmit={() => {}}
                        isTogglingEnabled={false}
                        disabled
                        overrideUrl={`${config.website}/for-women/job/${data.id}/`}
                    />
                    <Label label="Description" style={{ flexWrap: 'wrap', marginBottom: '1rem' }} />
                    <TextEditorInput name="htmlBody" label="Description" initialValue={data.htmlBody} handleSubmit={() => {}} isExisting={true} disabled />
                    <Fragment>
                        <Label label="Created">
                            <Date date={data.added} />
                        </Label>
                        <Label label="Updated">
                            <Date date={data.updated} />
                        </Label>
                        <Label label="Updated By">{data.updatedBy}</Label>
                    </Fragment>
                </Panel>
                {!isClient && (
                    <Panel title="Job Admin Info">
                        <Fragment>
                            <Label label="Source">{data.source}</Label>
                        </Fragment>
                    </Panel>
                )}
                <Button type="submit" disabled={true} theme="success">
                    {'Save not available'}
                </Button>
            </Fragment>
        </Fragment>
    );
};

const Logo = styled.div`
    max-width: 20rem;
    max-height: 12rem;
    margin-bottom: 1rem;
    background: #f1f1f1;
    display: flex;
    justify-content: center;
    padding: 1.5rem;
    box-sizing: border-box;

    img {
        object-fit: contain;
        max-width: 17rem;
        max-height: 9rem;
    }
`;

const Separator = styled.div`
    display: block;
    text-align: center;
    overflow: hidden;
    white-space: nowrap;

    > span {
        position: relative;
        display: inline-block;
    }

    > span:before,
    > span:after {
        content: '';
        position: absolute;
        top: 50%;
        width: 9999px;
        height: 1px;
        background: #eee;
    }

    > span:before {
        right: 100%;
        margin-right: 15px;
    }

    > span:after {
        left: 100%;
        margin-left: 15px;
    }
`;

const Url = styled.span`
    opacity: 0.5;
    margin-right: -0.5rem;
`;

const HeaderButtons = styled.div`
    button {
        margin-right: 1rem;
    }

    button: last-child;
     {
        margin-right: 0;
    }
`;

const InlineField = styled.span`
    display: inline-block;
    margin-left: -1rem;
`;

export default ViewEditAdd(Job)({ domain: 'Job' });
